import { useState, useEffect } from "react";
import { X, FlaskConical, Copy, Loader2 } from "lucide-react";
import { api, type CustomPrompts, type DefaultPromptsResponse } from "@/services/api";

interface CustomPromptsDiffModalProps {
  isOpen: boolean;
  onClose: () => void;
  customPrompts: CustomPrompts;
}

const SECTION_LABELS: Record<string, string> = {
  system_prompt: "System Prompt",
  SEE: "SEE - Transparency & Visibility",
  CHANGE: "CHANGE - Customizability & Control",
  ADAPT: "ADAPT - Vendor Agility & Innovation",
  USE: "USE - Genuine Utility & Value",
  LEARN: "LEARN - Transferable Knowledge",
  EXIT: "EXIT - Portability & Migration",
  executive_summary_prompt: "Executive Summary",
  key_questions_prompt: "Key Questions",
};

/**
 * Simple line-by-line diff. Returns array of { type, text } tuples.
 */
function computeLineDiff(
  original: string,
  modified: string
): Array<{ type: "same" | "removed" | "added"; text: string }> {
  const origLines = original.split("\n");
  const modLines = modified.split("\n");
  const result: Array<{ type: "same" | "removed" | "added"; text: string }> = [];

  const maxLen = Math.max(origLines.length, modLines.length);
  let oi = 0;
  let mi = 0;

  // Simple LCS-free approach: walk through both arrays
  while (oi < origLines.length || mi < modLines.length) {
    if (oi < origLines.length && mi < modLines.length) {
      if (origLines[oi] === modLines[mi]) {
        result.push({ type: "same", text: origLines[oi] });
        oi++;
        mi++;
      } else {
        // Look ahead for a match
        let foundOrig = -1;
        let foundMod = -1;
        for (let look = 1; look < Math.min(5, maxLen); look++) {
          if (foundMod === -1 && mi + look < modLines.length && origLines[oi] === modLines[mi + look]) {
            foundMod = mi + look;
          }
          if (foundOrig === -1 && oi + look < origLines.length && origLines[oi + look] === modLines[mi]) {
            foundOrig = oi + look;
          }
        }

        if (foundOrig !== -1 && (foundMod === -1 || foundOrig - oi <= foundMod - mi)) {
          // Lines were removed from original
          while (oi < foundOrig) {
            result.push({ type: "removed", text: origLines[oi] });
            oi++;
          }
        } else if (foundMod !== -1) {
          // Lines were added in modified
          while (mi < foundMod) {
            result.push({ type: "added", text: modLines[mi] });
            mi++;
          }
        } else {
          result.push({ type: "removed", text: origLines[oi] });
          result.push({ type: "added", text: modLines[mi] });
          oi++;
          mi++;
        }
      }
    } else if (oi < origLines.length) {
      result.push({ type: "removed", text: origLines[oi] });
      oi++;
    } else {
      result.push({ type: "added", text: modLines[mi] });
      mi++;
    }
  }

  return result;
}

export default function CustomPromptsDiffModal({
  isOpen,
  onClose,
  customPrompts,
}: CustomPromptsDiffModalProps) {
  const [defaultPrompts, setDefaultPrompts] = useState<DefaultPromptsResponse["prompts"] | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && !defaultPrompts) {
      setLoading(true);
      api.getDefaultPrompts()
        .then((res) => setDefaultPrompts(res.prompts))
        .catch((err) => console.error("Failed to load default prompts:", err))
        .finally(() => setLoading(false));
    }
  }, [isOpen, defaultPrompts]);

  if (!isOpen) return null;

  // Build diff sections
  const sections: Array<{ key: string; label: string; original: string; modified: string }> = [];

  if (defaultPrompts) {
    if (customPrompts.system_prompt) {
      sections.push({
        key: "system_prompt",
        label: SECTION_LABELS.system_prompt,
        original: defaultPrompts.system_prompt.editable,
        modified: customPrompts.system_prompt,
      });
    }
    if (customPrompts.criterion_prompts) {
      for (const [id, text] of Object.entries(customPrompts.criterion_prompts)) {
        if (text && defaultPrompts.criterion_prompts[id]) {
          sections.push({
            key: id,
            label: SECTION_LABELS[id] || id,
            original: defaultPrompts.criterion_prompts[id].editable,
            modified: text,
          });
        }
      }
    }
    if (customPrompts.executive_summary_prompt) {
      sections.push({
        key: "executive_summary_prompt",
        label: SECTION_LABELS.executive_summary_prompt,
        original: defaultPrompts.executive_summary_prompt.editable,
        modified: customPrompts.executive_summary_prompt,
      });
    }
    if (customPrompts.key_questions_prompt) {
      sections.push({
        key: "key_questions_prompt",
        label: SECTION_LABELS.key_questions_prompt,
        original: defaultPrompts.key_questions_prompt.editable,
        modified: customPrompts.key_questions_prompt,
      });
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-3xl mx-4 overflow-hidden flex flex-col" style={{ maxHeight: "85vh" }}>
        {/* Header */}
        <div className="bg-gradient-to-r from-[#063F6B] to-[#124C77] p-5 shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FlaskConical className="h-5 w-5 text-amber-300" />
              <h2 className="text-lg font-semibold text-white">Custom Prompts Used</h2>
            </div>
            <button
              onClick={onClose}
              className="text-white/70 hover:text-white transition-colors p-2 rounded-lg hover:bg-white/10 min-w-[40px] min-h-[40px] flex items-center justify-center"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Loading diff...</span>
            </div>
          ) : sections.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              No prompt modifications found.
            </p>
          ) : (
            sections.map((section) => {
              const diff = computeLineDiff(section.original, section.modified);
              return (
                <div key={section.key}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-foreground">{section.label}</h3>
                    <button
                      onClick={() => navigator.clipboard.writeText(section.modified)}
                      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                      title="Copy custom prompt text"
                    >
                      <Copy className="h-3 w-3" />
                      Copy
                    </button>
                  </div>
                  <div className="rounded-lg border border-border bg-muted/20 p-3 font-mono text-xs overflow-x-auto">
                    {diff.map((line, i) => (
                      <div
                        key={i}
                        className={`whitespace-pre-wrap ${
                          line.type === "removed"
                            ? "bg-red-100 dark:bg-red-950/40 text-red-700 dark:text-red-400"
                            : line.type === "added"
                            ? "bg-green-100 dark:bg-green-950/40 text-green-700 dark:text-green-400"
                            : "text-muted-foreground"
                        }`}
                      >
                        <span className="select-none opacity-50 mr-2">
                          {line.type === "removed" ? "-" : line.type === "added" ? "+" : " "}
                        </span>
                        {line.text || "\u00A0"}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-border p-4 shrink-0 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-foreground border border-border rounded-lg hover:bg-muted transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
